// ── Crowdfunding Configuration ────────────────────────────────────
import { PLATFORM_FEE_ADDRESS, BCH_DUST_LIMIT } from './platform';

export { PLATFORM_FEE_ADDRESS };

// Crowdfunding fee: 3% of each pledge (separate from 1% tip fee)
export const CROWDFUND_FEE_PERCENT = 0.03;

// Nostr event kinds and tags
export const CAMPAIGN_KIND = 30078;
export const CAMPAIGN_D_TAG_PREFIX = 'bchnostr/campaign/';
export const CAMPAIGN_TAG = 'bch-crowdfund';
export const PLEDGE_KIND = 9735; // reuse tip receipt kind for social proof
export const PLEDGE_TAG = 'bch-pledge';

// Campaign status — extended with lifecycle stages
export type CampaignStatus = 'active' | 'funded' | 'in_progress' | 'completed' | 'expired';

export interface CampaignData {
  title: string;
  description: string;
  goalSats: number;
  deadline: number; // unix timestamp
  bchAddress: string;
  image?: string;
  createdBy: string; // pubkey
  // Extended fields (optional for backwards compat)
  status?: CampaignStatus; // explicit status set by creator
  completedAt?: number;    // unix timestamp when marked complete
  completionProof?: CompletionProof;
}

// ── Campaign Updates ────────────────────────────────────────────
// Creator updates are kind 1 notes tagged with the campaign reference.
// This keeps them visible in the Nostr network while linking to the campaign.
export const CAMPAIGN_UPDATE_TAG = 'bch-campaign-update';

export interface CampaignUpdate {
  content: string;
  images?: string[];
  links?: string[];
  campaignId: string;
  createdAt: number;
  pubkey: string;
  eventId: string;
}

// ── Completion Proof ────────────────────────────────────────────
export interface CompletionProof {
  description: string;
  images?: string[];
  links?: string[];
  completedAt: number;
}

// ── Community Confirmation ──────────────────────────────────────
// Supporters react with a specific tag to confirm delivery
export const CONFIRMATION_TAG = 'bch-campaign-confirmed';

// ── Star Ratings ─────────────────────────────────────────────────
// Backers can rate campaigns 1-5 stars. Stored as kind 1985 (custom)
// with a rating value tag. One rating per user per campaign.
export const RATING_KIND = 1985;
export const RATING_TAG = 'bch-campaign-rating';

export interface CampaignRating {
  campaignId: string;
  pubkey: string;
  rating: number; // 1-5
  createdAt: number;
}

/** Parse a rating from a kind 1985 event */
export function parseRating(event: any): CampaignRating | null {
  if (event.kind !== RATING_KIND) return null;
  const hasTag = event.tags?.some((t: string[]) => t[0] === 't' && t[1] === RATING_TAG);
  if (!hasTag) return null;
  const ratingTag = event.tags?.find((t: string[]) => t[0] === 'rating');
  const rating = ratingTag ? parseInt(ratingTag[1]) : 0;
  if (rating < 1 || rating > 5) return null;
  const campaignRef = event.tags?.find((t: string[]) => t[0] === 'e')?.[1];
  if (!campaignRef) return null;
  return { campaignId: campaignRef, pubkey: event.pubkey, rating, createdAt: event.created_at };
}

/** Compute average rating from rating events (deduplicates per user, keeps latest).
 *  Excludes self-ratings from the campaign creator so they can't inflate their own score. */
export function computeAverageRating(ratings: CampaignRating[], creatorPubkey?: string): { average: number; count: number } {
  // Keep only the latest rating per user, exclude creator self-ratings
  const byUser = new Map<string, CampaignRating>();
  for (const r of ratings) {
    if (creatorPubkey && r.pubkey === creatorPubkey) continue; // skip self-rating
    const existing = byUser.get(r.pubkey);
    if (!existing || r.createdAt > existing.createdAt) {
      byUser.set(r.pubkey, r);
    }
  }
  const unique = [...byUser.values()];
  if (unique.length === 0) return { average: 0, count: 0 };
  const sum = unique.reduce((s, r) => s + r.rating, 0);
  return { average: sum / unique.length, count: unique.length };
}

/** Compute a creator's average rating across all their campaigns.
 *  Excludes self-ratings so creators can't inflate their own reputation. */
export function computeCreatorAverageRating(
  allRatings: CampaignRating[],
  creatorPubkey?: string,
): { average: number; totalRatings: number } {
  if (allRatings.length === 0) return { average: 0, totalRatings: 0 };
  // Deduplicate per user per campaign (latest wins), exclude creator self-ratings
  const key = (r: CampaignRating) => `${r.pubkey}:${r.campaignId}`;
  const byKey = new Map<string, CampaignRating>();
  for (const r of allRatings) {
    if (creatorPubkey && r.pubkey === creatorPubkey) continue; // skip self-rating
    const k = key(r);
    const existing = byKey.get(k);
    if (!existing || r.createdAt > existing.createdAt) {
      byKey.set(k, r);
    }
  }
  const unique = [...byKey.values()];
  if (unique.length === 0) return { average: 0, totalRatings: 0 };
  const sum = unique.reduce((s, r) => s + r.rating, 0);
  return { average: sum / unique.length, totalRatings: unique.length };
}

// ── Creator Reputation ──────────────────────────────────────────
export interface CreatorReputation {
  campaignsCreated: number;
  campaignsCompleted: number;
  totalRaisedSats: number;
  completionRate: number; // 0-100
  confirmations: number;  // total community confirmations across all campaigns
  averageRating: number;  // 0-5 (0 = no ratings)
  totalRatings: number;   // number of unique ratings across all campaigns
}

// ── Timeline Event Types ────────────────────────────────────────
export type TimelineEventType = 'created' | 'pledge' | 'milestone' | 'funded' | 'update' | 'status_change' | 'completed' | 'confirmed';

export interface TimelineEntry {
  type: TimelineEventType;
  timestamp: number;
  description: string;
  pubkey?: string;
  data?: any;
}

/** Calculate 3% crowdfunding fee */
export function calculateCrowdfundFee(amountSats: number): number {
  if (amountSats <= 0) return 0;
  return Math.ceil(amountSats * CROWDFUND_FEE_PERCENT);
}

/** Whether the fee is large enough to be a separate BCH tx output */
export function canSendCrowdfundFeeOutput(feeSats: number): boolean {
  return feeSats >= BCH_DUST_LIMIT;
}

/** Fee breakdown for a pledge amount */
export function pledgeFeeBreakdown(amountSats: number): {
  total: number;
  fee: number;
  net: number;
  feeAsOutput: boolean;
} {
  const fee = calculateCrowdfundFee(amountSats);
  return {
    total: amountSats,
    fee,
    net: amountSats - fee,
    feeAsOutput: canSendCrowdfundFeeOutput(fee),
  };
}

/** Get campaign status based on data.
 *  Priority: explicit creator status > funding state > time-based */
export function getCampaignStatus(campaign: CampaignData, raisedSats: number): CampaignStatus {
  // Creator-set statuses take precedence
  if (campaign.status === 'completed') return 'completed';
  if (campaign.status === 'in_progress') return 'in_progress';

  const now = Math.floor(Date.now() / 1000);
  if (campaign.goalSats > 0 && raisedSats >= campaign.goalSats) return 'funded';
  if (now > campaign.deadline) return 'expired';
  return 'active';
}

/** Build a timeline from campaign events */
export function buildTimeline(
  campaign: CampaignData,
  campaignEvent: any,
  pledges: any[],
  updates: CampaignUpdate[],
  confirmations: any[],
  raisedSats: number,
): TimelineEntry[] {
  const entries: TimelineEntry[] = [];

  // Campaign created
  entries.push({
    type: 'created',
    timestamp: campaignEvent.created_at,
    description: 'Campaign created',
    pubkey: campaignEvent.pubkey,
  });

  // Funding milestones (25%, 50%, 75%, 100%)
  if (campaign.goalSats > 0) {
    let runningTotal = 0;
    const milestones = [25, 50, 75, 100];
    const milestoneSats = milestones.map(m => Math.round(campaign.goalSats * m / 100));
    const hitMilestones = new Set<number>();

    const sortedPledges = [...pledges].sort((a, b) => a.created_at - b.created_at);
    for (const pledge of sortedPledges) {
      const amountTag = pledge.tags?.find((t: string[]) => t[0] === 'amount');
      const amount = amountTag ? parseInt(amountTag[1]) || 0 : 0;
      runningTotal += amount;

      for (let i = 0; i < milestones.length; i++) {
        if (!hitMilestones.has(milestones[i]) && runningTotal >= milestoneSats[i]) {
          hitMilestones.add(milestones[i]);
          entries.push({
            type: milestones[i] === 100 ? 'funded' : 'milestone',
            timestamp: pledge.created_at,
            description: milestones[i] === 100
              ? 'Goal reached!'
              : `${milestones[i]}% funded`,
          });
        }
      }
    }
  }

  // Creator updates
  for (const update of updates) {
    entries.push({
      type: 'update',
      timestamp: update.createdAt,
      description: update.content.slice(0, 200),
      pubkey: update.pubkey,
      data: update,
    });
  }

  // Status changes
  if (campaign.status === 'in_progress') {
    entries.push({
      type: 'status_change',
      timestamp: campaign.completedAt || campaignEvent.created_at,
      description: 'Creator marked project as in progress',
      pubkey: campaignEvent.pubkey,
    });
  }

  // Completion
  if (campaign.status === 'completed' && campaign.completedAt) {
    entries.push({
      type: 'completed',
      timestamp: campaign.completedAt,
      description: 'Project completed',
      pubkey: campaignEvent.pubkey,
      data: campaign.completionProof,
    });
  }

  // Community confirmations
  for (const conf of confirmations) {
    entries.push({
      type: 'confirmed',
      timestamp: conf.created_at,
      description: 'Supporter confirmed delivery',
      pubkey: conf.pubkey,
    });
  }

  return entries.sort((a, b) => a.timestamp - b.timestamp);
}

/** Calculate creator reputation from their campaigns */
export function calculateReputation(
  campaigns: { data: CampaignData; raisedSats: number; confirmationCount: number }[],
  ratingData?: { average: number; totalRatings: number },
): CreatorReputation {
  const created = campaigns.length;
  const completed = campaigns.filter(c => c.data.status === 'completed').length;
  const totalRaised = campaigns.reduce((sum, c) => sum + c.raisedSats, 0);
  const confirmations = campaigns.reduce((sum, c) => sum + c.confirmationCount, 0);
  // Only count campaigns that are funded/completed/in_progress for completion rate denominator
  // (don't penalize for still-active campaigns)
  const eligible = campaigns.filter(c => {
    const s = c.data.status;
    return s === 'completed' || s === 'in_progress' || s === 'funded';
  }).length;
  const completionRate = eligible > 0 ? Math.round((completed / eligible) * 100) : 0;

  return {
    campaignsCreated: created,
    campaignsCompleted: completed,
    totalRaisedSats: totalRaised,
    completionRate,
    confirmations,
    averageRating: ratingData?.average || 0,
    totalRatings: ratingData?.totalRatings || 0,
  };
}

/** Parse a campaign update from a kind 1 event */
export function parseCampaignUpdate(event: any): CampaignUpdate | null {
  const campaignRef = event.tags?.find((t: string[]) => t[0] === 'e')?.[1];
  if (!campaignRef) return null;

  // Extract image URLs from content
  const imageRegex = /https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp)(\?[^\s]*)?/gi;
  const linkRegex = /https?:\/\/[^\s]+/gi;
  const images = event.content.match(imageRegex) || [];
  const allLinks = event.content.match(linkRegex) || [];
  const links = allLinks.filter((l: string) => !images.includes(l));

  return {
    content: event.content,
    images: images.length > 0 ? images : undefined,
    links: links.length > 0 ? links : undefined,
    campaignId: campaignRef,
    createdAt: event.created_at,
    pubkey: event.pubkey,
    eventId: event.id,
  };
}

/** Generate a unique campaign d-tag */
export function campaignDTag(id: string): string {
  return CAMPAIGN_D_TAG_PREFIX + id;
}

// ── Featured Campaigns (Auction System) ──────────────────────────
// Max 5 featured slots. When all slots are full, new bids must beat
// the lowest current bid to claim a spot. Outbid campaigns drop to
// the "Boosted" section. Existing pre-auction featured campaigns are
// grandfathered until they expire naturally (soft transition).

export const FEATURED_CAMPAIGN_KIND = 30078;
export const FEATURED_CAMPAIGN_D_TAG_PREFIX = 'bchnostr/featured-campaign/';
export const FEATURED_CAMPAIGN_TAG = 'bch-featured-campaign';

/** Maximum number of featured slots available at any time */
export const FEATURED_MAX_SLOTS = 5;

/** Minimum bid to feature when slots are open (no one to outbid) */
export const FEATURED_MIN_BID_SATS = 25000;

/** Legacy tiers kept for backwards compat with existing featured events */
export const FEATURED_TIERS = [
  { id: '3d', label: '3 Days', duration: 3 * 86400, priceSats: 25000, emoji: '⭐' },
  { id: '7d', label: '7 Days', duration: 7 * 86400, priceSats: 50000, emoji: '\u{1F525}' },
  { id: '14d', label: '14 Days', duration: 14 * 86400, priceSats: 80000, emoji: '\u{1F680}' },
];

/** Duration for new auction-based featured slots: 7 days */
export const FEATURED_AUCTION_DURATION = 7 * 86400;

export interface FeaturedCampaignData {
  campaignId: string;
  tier: string;
  priceSats: number;
  expiresAt: number;
  featuredBy: string; // pubkey
}

export function isFeaturedActive(event: any): boolean {
  try {
    const data: FeaturedCampaignData = JSON.parse(event.content);
    return data.expiresAt > Math.floor(Date.now() / 1000);
  } catch { return false; }
}

export function parseFeaturedData(event: any): FeaturedCampaignData | null {
  try { return JSON.parse(event.content); } catch { return null; }
}

/** Compute auction state from active featured events.
 *  Returns the sorted list of active bids (highest first)
 *  and the minimum bid needed to claim a slot. */
export function computeAuctionState(activeFeatured: FeaturedCampaignData[]): {
  /** All active bids sorted by priceSats descending */
  bids: FeaturedCampaignData[];
  /** Number of slots currently occupied */
  slotsUsed: number;
  /** True if all 5 slots are taken */
  slotsFull: boolean;
  /** Minimum sats to claim a featured slot right now */
  minimumBid: number;
  /** The lowest current bid (the one that would be outbid), or null */
  lowestBid: FeaturedCampaignData | null;
} {
  // Dedupe by campaignId — keep the highest bid per campaign
  const byId = new Map<string, FeaturedCampaignData>();
  for (const bid of activeFeatured) {
    const existing = byId.get(bid.campaignId);
    if (!existing || bid.priceSats > existing.priceSats) {
      byId.set(bid.campaignId, bid);
    }
  }

  const bids = [...byId.values()].sort((a, b) => b.priceSats - a.priceSats);
  const slotsUsed = bids.length;
  const slotsFull = slotsUsed >= FEATURED_MAX_SLOTS;
  const lowestBid = slotsFull ? bids[bids.length - 1] : null;

  // If slots are open, minimum is the base price.
  // If full, minimum is lowest bid + 1 sat (must outbid).
  const minimumBid = slotsFull && lowestBid
    ? lowestBid.priceSats + 1
    : FEATURED_MIN_BID_SATS;

  return { bids, slotsUsed, slotsFull, minimumBid, lowestBid };
}

/** Get the top N featured campaign IDs (the ones that actually get the featured badge).
 *  During soft transition: if ≤ MAX_SLOTS active, all get featured.
 *  If > MAX_SLOTS, only the top N by bid amount get featured; rest are "outbid". */
export function getTopFeaturedIds(activeFeatured: FeaturedCampaignData[]): {
  featuredIds: Set<string>;
  outbidIds: Set<string>;
} {
  // Dedupe by campaignId — keep highest bid
  const byId = new Map<string, FeaturedCampaignData>();
  for (const bid of activeFeatured) {
    const existing = byId.get(bid.campaignId);
    if (!existing || bid.priceSats > existing.priceSats) {
      byId.set(bid.campaignId, bid);
    }
  }

  const sorted = [...byId.values()].sort((a, b) => b.priceSats - a.priceSats);
  const featuredIds = new Set<string>();
  const outbidIds = new Set<string>();

  sorted.forEach((bid, i) => {
    if (i < FEATURED_MAX_SLOTS) {
      featuredIds.add(bid.campaignId);
    } else {
      outbidIds.add(bid.campaignId);
    }
  });

  return { featuredIds, outbidIds };
}

